// Ski v3 parameter register. Every tunable constant of the engine lives here.
// [S] = sourced (see docs/audits/2026-09-26-ski-scoring-deep-dive.md research notes),
// [E] = engineering default fitted by the benchmark suite and later by labels.
// Any change must bump SKI_V3_PARAMS_VERSION and carry a one-line reason.
//
// What stays out of this register (not tunables): physical constants (PHYSICS below),
// published formula coefficients kept inside their formula (Stull 2011 wet-bulb, Magnus
// vapour pressure, Erbs 1982, Haurwitz 1945, Kasten and Czeplak 1980, Steadman 1994,
// Abramowitz and Stegun, Lanczos), the member quadrature (math.ts MEMBER_Z and
// MEMBER_WEIGHTS, contract Section 5), the checkpoint stored format (checkpoint.ts field
// tables and CHECKPOINT_FORMAT, which need a new SNOW_ENGINE_VERSION to change), unit
// conversions, numerical guards (epsilons, iteration caps, cache sizes) and neutral
// fallbacks for non-finite inputs (invariant 8), which a valid run never reaches.
import type { AspectSector, CoverClass } from "./types";

export const SKI_V3_PARAMS_VERSION = "ski-v3-params-2026-09-27.3"; // operators wired 27 Sep (.3): the engine now calls the .2 operators (model-depth hop sigma, station record weight, station SWE mass update in place of the depth update when the pillow agrees with the station snow height (stationSweForUpdate, with the new stationSweNoiseKgM2 bound below 5 cm), satellite product temper, run-versus-band satellite rule keyed by the new satellitePixelSource so HR-WSI and Sentinel-2 SCL count as one scene), and confidence class A counts only full-weight station records (not SNOWPACK model rows); no existing value changed, and outputs change only for inputs that carry hopKm, a station quality or SWE, a tempered satellite product or run and band passes of the same pixels; earlier engine inputs 27 Sep (.2): railway lift capacity 1200 and railway downloadable (terrain), station site-kind and quality exclusions and satellite product precedence (evidence selection), new NWP hop, station SWE and satellite product temper operators (neutral until the engine calls them); earlier (.1-r1): tuned rounds 1 to 8 (benchmark tuning, see "tuned round N" comments; round 5 extended burialCm to the wet memory; round 6 area best quarter off-piste, worked snow, grooming order; round 7 probabilistic touring wet passage; round 8 descent weather factor in touring); review fixes 27 Sep ("review fix 27 Sep" comments): visCentreM 2500 -> 1100, closureGraceH removed, new leeSupplyRatio, leeSupplyTauH, pgMergePasses, powderDepthSoftCm, powderDepthHardCm, maxBaseAgeD, skiedShareRamp, skiableVerticalM, missingDayW, stationRepresentativenessScaleM, stationDepthGradientPerM, allClosedOpenFraction, resortClosureRampH, runClosedStatusWeight, operatorShareA, missing-weather class and envelope, checkpoint memory limits

/** Physical constants (not tunables). */
export const PHYSICS = {
  latentHeatFusionJPerKg: 334000, // latent heat of fusion of ice (J/kg)
  latentMJPerMm: 0.334, // physical: latent heat of fusion, 334 kJ per kg (1 mm of melt over 1 m2)
  isaP0Hpa: 1013.25, // [S] ICAO standard atmosphere
  isaT0K: 288.15, // [S]
  isaT0C: 15, // [S]
  isaLapseKPerM: 0.0065, // [S]
  isaExponent: 5.25588, // [S] g M / (R L)
  magnusA: 17.62, // [S] Sonntag 1990, over water
  magnusB: 243.12, // [S]
  magnusE0Hpa: 6.112, // [S] Sonntag 1990: saturation vapour pressure over water at 0 C (hPa)
  solarConstantWm2: 1361, // [S] Kopp and Lean 2011
  earthRadiusKm: 6371.0088, // [S] IUGG mean Earth radius
} as const;

/**
 * Visibility (m) implied by a WMO weather code, transcribed unchanged from the table in
 * lib/conditions/piste-scoring.ts so behaviour stays familiar. [E]
 */
const WEATHER_CODE_VISIBILITY_M: Readonly<Partial<Record<number, number>>> = {
  0: 50000, 1: 40000, 2: 30000, 3: 15000,
  45: 200, 48: 150,
  51: 5000, 53: 3000, 55: 1500,
  56: 1500, 57: 1200,
  61: 4000, 63: 2000, 65: 800,
  66: 2000, 67: 1000,
  71: 2500, 73: 1200, 75: 500,
  77: 3000,
  80: 4000, 81: 2500, 82: 800,
  85: 2000, 86: 700,
  95: 1000, 96: 800, 99: 500,
};

export const SKI_V3_PARAMS = {
  members: {
    tempOffsetC: 0.5, // [E] per unit Z
    precipLogSpread: 0.25, // [E] precip x exp(0.25 Z)
    cloudOffset: 0.08, // [E] cloud fraction per unit Z
    sweLogSpreadIcon: 0.45, // [E] initial log spread, ICON-family regions
    sweLogSpreadGfs: 0.7, // [E] initial log spread, GFS regions
  },
  forcing: {
    lapseCPerKm: -6.5, // [S] standard atmosphere
    dewLapseCPerKm: -2.0, // [E] when no humidity profile exists
    surfaceBlendScaleM: 300, // [E] weight of the 2 m value near the fetch point
    snowFractionCentreC: 0.7, // [E] anchored to ~1 C 50% threshold [S Jennings 2018]
    snowFractionWidthC: 0.45, // [E]
    windElevationPerM: 0.0004, // existing
    windElevationMin: 0.7,
    windElevationMax: 1.4,
    ridgeWindFactor: 1.3, // [E]
    horizonDeg: 8, // [E] placeholder until DEM horizons
    groundReflectance: 0.6, // [E]
    immersionCentrePct: 50, // [E] tuned round 3: the rescaled immersion logistic (width 45) is centred at 50 % so it flattens towards full cloud; centred at 80 it was steepest at 85 to 95 % low cloud, where a 5 % cloud change moved a half-immersed top band's visibility quality by 15 % (smoothness invariant, Rockies); was 80
    immersionWidthPct: 45, // [E] tuned round 2: the normalised visibility quality (visClearRefM) steepened V by about 8 %, so 35 let a 5 % cloud change move Rockies piste hours by up to 4/100 (smoothness invariant); 45 keeps them under 3; round 1 set 35 (decision (a), at least 25), was 7
    lclMPerC: 125, // [S] lifting condensation level ~125 m per C dew-point depression
    calmZeroKmh: 25, // [E] calm = clamp((25 - U)/15)
    calmSpanKmh: 15, // [E]
    snowfallCmPerMm: 0.7, // [S] Open-Meteo convention
    // Moved from forcing.ts (consolidation, 26 Sep).
    wetBulbPressureCoeffCPerHpa: 0.0003, // [E] contract section 6: Tw -= 0.0003 (1013 - p)
    wetBulbReferenceHpa: 1013, // [E]
    horizonRampDeg: 4, // [E] smooth horizon: direct beam ramps in over horizonDeg -/+ 2 deg
    profileEdgeFadeM: 300, // [E] beyond the outermost pressure level, profile influence fades to the lapse value
    immersionBaseWidthM: 100, // [E] logistic width about the LCL cloud base
    highCloudWeight: 0.8, // [E] contract: clear = 1 - max(low, mid, 0.8 high)/100
    dniCapWm2: 1100, // [E] contract: DNI from direct/sin(elev) capped
    diffuseFractionSoftWm2: 5, // [E] regularises D/G near sunrise and sunset
    gustFactor: 1.5, // [E] gust from mean wind when the gust field is missing
    missingWindKmh: 15, // [E] neutral wind when missing: never calm; radiative calm factor 2/3
    missingCloudPct: 50, // [E] neutral cloud when missing (never clear)
    missingRhPct: 85, // [E] neutral relative humidity when missing
    clearnessMinSinEl: 0.05, // [E] floor on sin(elevation) in the Erbs clearness index near the horizon
    /** Weather code derived from the unit's own forcing when the source code is missing. */
    derivedCode: {
      precipMinMm: 0.1, // [E] precipitation water (mm/h) below which the derived code is dry
      snowSlightMm: 1, // [E] snow water (mm/h): slight (71) below, moderate (73) up to snowHeavyMm
      snowHeavyMm: 3, // [E] heavy snow (75) from here
      rainSlightMm: 2.5, // [S] AMS rain intensity: slight (61) below 2.5 mm/h
      rainHeavyMm: 7.6, // [S] heavy rain (65) from 7.6 mm/h
      fogImmersed: 0.5, // [E] cloud immersion above which the derived code is fog (45)
      overcastPct: 87.5, // [S] 7 oktas: overcast (3)
      brokenPct: 50, // [S] 4 oktas: partly cloudy (2)
      scatteredPct: 12.5, // [S] 1 okta: mainly clear (1)
    },
  },
  snowpack: {
    surfaceLayerKgM2: 300, // [E] tuned round 4: new; the top of the base (about 80 cm of settled snow) whose density, pore water and made-snow character the surface sees: density mixing and water retention act on this layer only, so extra old snow below it changes no score (invariant 3: a 1,300 kg/m2 deep pack pulled down by model depth stays above it); thinner bases behave exactly as before
    freshRhoBase: 67.92, // [S] Hedstrom-Pomeroy
    freshRhoScale: 51.25, // [S]
    freshRhoDenom: 2.59, // [S]
    freshRhoWindPerKmh: 4, // [E] above 15 km/h
    freshRhoWindThresholdKmh: 15, // [E]
    freshRhoMin: 50,
    freshRhoMax: 250,
    freshSettleTargetRho: 250, // [E]
    freshSettleTauColdH: 48, // [E] below -5 C
    freshSettleTauWarmH: 18, // [E] above 0 C
    mergeTauH: 96, // [E]
    mergeTauDenseH: 24, // [E] once fresh rho >= 230
    mergeDenseRho: 230, // [E]
    baseRhoColdTarget: 300, // [E]
    baseRhoWarmTarget: 450, // [E]
    baseRhoTauH: 240, // [E] 10 days
    meltTfMmPerCH: 0.05, // [S] Pellicciotti 2005
    meltSrfMmPerWm2H: 0.0094, // [S] Pellicciotti 2005
    meltRampLowC: -3, // [E] no radiative melt below -3 C air
    meltRampSpanC: 4, // [E] full at +1 C
    albedoFresh: 0.85, // [S]
    albedoRefreshMm: 10, // [E]
    albedoDryDecayPerDay: 0.008, // [S] ISBA / Douville 1995
    albedoDryFloor: 0.6, // [E]
    albedoWetTarget: 0.5, // [S]
    albedoWetRatePerDay: 0.24, // [S]
    albedoMadeFactor: 1.5, // [E] made snow decays faster (unmeasured)
    refreezeMmPerCH: 0.22, // existing
    liquidHoldFraction: 0.06, // [E] of ice mass
    snowAgeResetCm: 5, // [E] age x exp(-hn/5)
    // Moved from snowpack.ts (consolidation, 26 Sep).
    settleWarmFromC: -5, // [E] fresh settlement time constant ramps from cold (48 h) to warm (18 h) over [-5, 0] C
    settleWarmSpanC: 5, // [E]
    mergeDenseWidthRho: 5, // [E] logistic width of the fresh-merge switch (96 h to 24 h) about 230 kg/m3, replacing a hard step
    baseRhoRampLowC: -10, // [E] base density target ramps from cold (300) to warm (450) over [-10, 0] C
    baseRhoRampSpanC: 10, // [E]
    bulkRhoMin: 50, // [E] bulk density bounds for the base layer (kg/m3)
    bulkRhoMax: 700, // [E]
    meltWetScaleMm: 0.25, // [E] albedo decay blends dry and wet regimes by 1 - exp(-melt/scale) (mm/h)
    initAlbedo: 0.79, // [E] tuned round 3: albedo of an initialised store of unknown history, consistent with initSnowAgeD (7 days of dry decay from 0.85 at 0.008 per day); at 0.75 a cold start sat 0.04 below a chained run and flipped a sunny unit between refrozen crust and corn (chained cold-start test); was 0.75
    initSnowAgeD: 7, // [E] snow age of an initialised store of unknown history
    initFreshRho: 100, // [E] fresh layer density when initStore gets freshMm without freshRho (kg/m3)
    defaultSeasonNorth: ["12-01", "04-15"] as readonly [string, string], // [E] season used by seasonFraction when a location has no season dates (MM-DD)
    defaultSeasonSouth: ["06-15", "10-05"] as readonly [string, string], // [E]
  },
  wind: {
    freshThresholdMs: 5, // [S] fresh snow moves at 4-6 m/s
    ageTauH: 24, // [E]
    transportMmPerMs: 0.8, // [E]
    transportMaxRho: 200, // [E]
    exposureCos: 0.25, // [E]
    treedFactor: 0.2, // [E]
    windwardCos: 0.3, // [E]
    leeCos: -0.3, // [E]
    leeDepositFraction: 0.6, // [E]
    leeDepositRho: 300, // [E]
    idxRateMs: 5, // [E]
    idxBuryMm: 5, // [E]
    wetDriftWidthC: 0.5, // [E] tuned round 3: new; drift and wind packing fade out as air temperature passes 0 C (logistic width), wet snow does not drift
    wetDriftLiquidShare: 0.01, // [E] tuned round 3: new; liquid water share of the pack (1 %) that e-folds drift and wind packing
    idxRainResetMm: 3, // [E] tuned round 3: new; rain erases the wind index, e-folding per 3 mm (wet, rounded grains refreeze as a rain crust, not wind buff or sastrugi)
    // Moved from snowpack.ts (consolidation, 26 Sep).
    liPomeroyA: 9.43, // [S] Li and Pomeroy 1997 threshold for aged dry snow: U_LP = a + b T + c T^2 (m/s)
    liPomeroyB: 0.18, // [S]
    liPomeroyC: 0.0033, // [S]
    transportRhoWidth: 10, // [E] logistic width of the "transport only while freshRho < 200" cut-off (kg/m3)
    transportRampCos: 0.4, // [E] smoothstep span in cos(aspect - wind) of the windward and lee ramps (contract 0.4)
    leeSupplyRatio: 2, // [E] review fix 27 Sep: new; the lee deposit per hour draws on an upwind supply of twice the snow falling that hour (the fetch is a few times the slope length)...
    leeSupplyTauH: 8, // [E] review fix 27 Sep: new; ...plus the loose snow of the fetch, taken as the lee's own fresh layer over 8 h, so a dry gale after a storm keeps loading for most of a day; with the transport gate fixed and no supply limit a 60 to 80 km/h storm loaded lee slopes 3.3 to 4.5 times the snowfall (now about 2.9 times, rubric 1.5 to 3), and the supply does not depend on wind speed, so loading stays monotone in it
  },
  snowmaking: {
    onsetWetBulbC: -2.5, // [S] -2 to -4 C
    onsetWidthC: 0.5, // [E]
    windLimitKmh: 15, // [S] Crocus 4.2 m/s
    windWidthKmh: 3, // [E]
    gunsPerHa: 3, // [S]
    efficiency: 0.8, // [S] mean loss 21%
    fanGunA: 3.94, // [S] Olefs 2010
    fanGunB: 4.83, // [S]
    snowDensityToKgM2: 0.04, // 400 kg/m3 / 1e4 m2 per ha
    capacityKgM2H: 1.25, // [E] ~5 days for 150 kg/m2
    capacityMemberLogSpread: 0.3, // [E]
    baseTargetKgM2: 150, // [S] Spandre 2019
    basePhaseLeadDays: 35, // [E]
    maintenanceMaxDepthM: 0.6, // [S]
    maintenanceNightStart: 18, // [S] Crocus-Resort
    maintenanceNightEnd: 8, // [S]
    madeSnowRho: 440, // [S] 442 +- 13
    coldStartFraction: 0.35, // [E] prior made mass per window hour share
    // Moved from snowpack.ts (consolidation, 26 Sep).
    basePhaseTrailDays: 30, // [E] base production window ends this many days after season start; maintenance follows
    maintenanceStopMonthNorth: 3, // [S] contract 7.1: maintenance until the end of February (first month without it, NH)
    maintenanceStopMonthSouth: 9, // [S] until the end of August (SH)
    maintenanceRampDays: 30, // [E] tuned round 3: new; the cold-start maintenance top-up (priorMaintenanceSweKgM2) ramps in over the first 30 days of the maintenance window
    madeAlbedo: 0.8, // [E] albedo of new machine snow; made snow refreshes the surface towards this
    missingWindKmh: 10, // [E] wind assumed by the snowmaking wind factor when the wind field is missing (km/h)
  },
  grooming: {
    rhoStart: 450, // [S] Keller 2004, SLF
    rhoSeasonGain: 150, // [S]
    minSweKgM2: 20, // [S] Crocus-Resort
    scheduleAWeight: 0.6, // [E] waits for refreeze
    scheduleBHour: 20, // [E] fixed shift
    scheduleAEarliest: 17,
    scheduleALatest: 23,
    secondPassHour: 5,
    secondPassLowCm: 2, // [E]
    secondPassSpanCm: 6, // [E]
    tillCrustCm: 1, // [E]
    tillIceFactor: 0.35, // [E]
    wetThetaPct: 3, // [E]
    tilledWetMemMm: 10, // [E] tuned round 1: wet memory (mm of melt or rain, 36 h) at which a tilled hard layer becomes fully loose granular; a thin sun crust tills into corduroy (P1 sunny aspects read tilled granular every morning)
    // Moved from surface.ts (consolidation, 26 Sep).
    tillCrustWidthCm: 0.3, // [E] smooth version of "zf > 1" (centre tillCrustCm)
    tillIceCentre: 0.3, // [E] an existing ice layer is tilled too (tilling turns ice into granular)
    tillIceWidth: 0.1, // [E]
    tillFrozenChopCentre: 0.3, // [E] frozen chop is tilled flat
    tillDryWidthPct: 0.5, // [E] smooth version of "theta < 3" (centre wetThetaPct)
    glazeWidthPct: 0.5, // [E] smooth version of "theta >= 3"
    glazeIceGain: 0.85, // [E] contract 0.5 at the refreeze; applied at grooming and raised because the liquid-gated refreeze adds little afterwards
    glazeFlagWeight: 0.5, // [E] the informational glazePending flag is set once weight x glaze weight exceeds this
    scheduleAReadyTsC: -1, // contract: schedule A waits for Ts < -1 C
    scheduleAReadyGlaze: 0.1, // [E] and for the glaze weight to be below 0.1 (theta < ~1.9 %)
    scheduleAReadyTill: 0.85, // [E] and for the till outcome to be decided: till weight >= 0.85 or crust weight < 0.1
    scheduleAReadyNoCrust: 0.1, // [E]
    eveningGuardH: 6, // [E] groomed this evening if hSinceGroom <= (hour - 17) + 6
    groomOpCentre: 0.5, // contract: grooming only while pi_op > 0.5 (smoothstep over 0.45..0.55)
    groomOpSpan: 0.1, // [E]
    groomSweSpan: 10, // [E] smoothstep over 15..25 kg/m2 around the 20 kg/m2 minimum [S]
  },
  surface: {
    energyTempCoeff: 10, // [E] tuned round 1: W/m2 per C; sensible heat (about 5 at light wind) plus incoming longwave (about 3) plus latent loss in cold dry air, so cold sun no longer melts groomers at -8 C (P1, T5, O7); was 5
    longwaveClearLoss: 85, // [E] tuned round 1: clear-sky net longwave of a melting surface is about -90 to -110 W/m2 in dry alpine air; 60 let cold sun melt groomers (A7, P13 read corn at -10 C); was 60
    longwaveBaseLoss: 15, // [E]
    latentWm2PerHpa: 10, // [E] tuned round 3: new; latent heat flux per hPa of vapour pressure difference between the surface (saturated at Ts) and the air (rho L_s C_E U 0.622/p, about 10 with C_E 0.002 and U 2 m/s at 780 hPa); sublimation into cold dry air keeps sunny midwinter groomers from melting at -5 to -10 C (P1, P14, O3 day 7, O7)
    warmPerWm2PerH: 0.086, // 20 kg/m2 top layer
    crustThawThetaF: 0.1, // [E] natural snow: 1 mm of melt thaws 1 cm of refrozen crust
    crustThawThetaFPiste: 0.04, // [E] tuned round 3: piste crusts thaw faster again (1 mm of melt thaws 2.5 cm) as traffic breaks the softening crust; north-facing afternoon corn still held at 0.06 (P7 afternoon 40 to 50, P12); round 2 set 0.06 (new)
    thetaPerMm: 3.33, // 3 cm layer
    wetDepthCmPerMm: 2.5, // [S] 1 mm wets ~2.5 cm at 4% holding
    radiativeNightC: 3, // [S] Raleigh 2013
    dayWeightWm2: 60, // [E] tuned round 1: in daylight the surface relaxes towards air temperature once about 60 W/m2 is absorbed (diffuse sky and turbulent heat reach shaded slopes too); at 150 shaded groomers stayed near the night dew-point value all day; was 150
    relaxTauH: 2, // [E]
    thetaIrrPct: 4, // [E]
    drainTauH: 1, // [E]
    drainTauIceH: 3, // [E]
    refreezeCoefPiste: 1.2, // [E] Stefan, k ~0.53 W/m/K [S Keller]
    refreezeCoefNatural: 0.7, // [E] k ~0.14 W/m/K
    thetaFreezeScaleC: 4, // [E]
    wetMemTauH: 36, // [E]
    iceWetMemMm: 15, // [E] tuned round 1: ice needs about 15 mm of refrozen water (rain, a warm day); a sunny winter day's few mm of melt refreezes as a crust, not boilerplate (P1, P6, P14); was 8
    chopFrozenWetMemMm: 6, // [E]
    maskNewSnowCm: 3, // [E]
    trafficCompactPasses: 400, // [E]
    pgMergePasses: 800, // [E] review fix 27 Sep: new; on ungroomed pistes (Pg) skier traffic works new snow into the old base, exp(-passes/800) per hour, alongside the store's fresh-merge time constant; before, Pg new snow never aged and a week-old storm still read as new snow on moguls
    sinterBaseH: 2.5, // [E] anchored to Fauve 2004
    sinterTempCoeff: 0.05, // [E] tuned round 1: cold slows sintering by exp(0.05 (-Ts)); at 0.09 a -22 C skin (colder than the groomed layer it stands for) left night-groomed snow only 60 % sintered at opening; was 0.09
    passesPerHour: 70, // [E] tuned round 1: at traffic 1, crowd 1, diurnal 1; at 100 an ordinary weekday skied corduroy and fresh snow off by mid-morning (P1, A6), leaving crowd 2 as the busy case (P13, A7); was 100
    chopPasses: 900, // [E] tuned round 2: ~10^2 to 10^3 passes [S Physics Today]; fresh snow on corduroy stays hero snow through the first lift hours (A6 post 94 to 100 to 11:00 read 93 at 600); round 1 set 600, was 150
    scrapePasses: 220, // [E] tuned round 6: the worked-snow firmness (below) now hardens the busy dry spell, so polish can build a little slower (P13, A7 55 to 60); was 180. Tuned round 3: the latent heat flux (round 3) keeps cold busy groomers dry, so traffic polish carries the busy afternoon (P13, A7 55 to 60); round 1 set 250, was 400
    tilledDecayPasses: 200, // [E]
    gunsRunningFactor: 0.97, // [E]
    liftOpen: 8.5, // [E] default local hours
    liftClose: 16.5, // [E]
    // Moved from surface.ts SURFACE_LOCAL_PARAMS (consolidation, 26 Sep). Every value is an
    // engineering default [E] unless tagged [S].
    energyBlendWm2: 10, // [E] logistic width blending the warming (Q > 0) and cooling (Q < 0) regimes, so Ts has no jump at Q = 0
    drainK: 0.3, // [E] gravity drainage d(theta)/dt = -k (theta - theta_irr)^3 per hour, Colbeck 1972 S^3 law [S]; replaces the linear tau_d
    iceThawMm: 4, // [E] melt thaws an icy crust: ice and frozen chop e-fold per 4 mm of melt
    burialCm: 8, // [E] new snow buries the crust, wet layer, ice, frozen chop, scrape and (since round 5) the wet memory (e-fold depth)
    chopBuryCm: 10, // [E] new snow fills chop and moguls
    newSnowThetaCm: 3, // [E] new snow replaces the wet 3 cm surface layer
    snowResetCm: 1, // [E] smooth reset of hoursSinceSnow and nPassSinceSnow by new snow
    wetDepthMaxCm: 100, // [E] cap on the unfrozen wetted depth
    liquidGatePct: 0.5, // [E] ice forms in proportion to 1 - exp(-theta/0.5): liquid must be present to refreeze
    thetaMaxPct: 40, // numerical cap on surface liquid water
    refreezeMemoryTauH: 24, // [E] fdh is a leaky memory of freezing degree-hours (steady state 24.5 x -Ts)
    refreezeMemoryTauNaturalH: 12, // [E] tuned round 1: new; natural snow's refreeze memory: with 24 h the night before last still counted as last night's refreeze, so a warm cloudy night read over-soft instead of isothermal (T2)
    freezeOnsetC: 0.5, // [E] freezing degrees ramp in over 0..-0.5 C (sensible heat of the top layer first)
    isoFdhC: 25, // [E] recent freezing (C h) below which a wet pack counts as isothermal (no refreeze last night)
    isoFdhNaturalC: 35, // [E] tuned round 1: new; natural snow (lower density, deeper wetting) needs more freezing degree-hours than a groomed piste to count as refrozen (T2)
    isoFdhWidth: 15, // [E] tuned round 1: smoothness invariant (0.5 C over a night moves fdh by 5 to 10 C h; width 8 flipped the isothermal split and the touring E3 term); was 8
    deepWetCm: 15, // [E] wetted depth beyond which unsupported wet snow is slush
    deepWetWidthCm: 4, // [E]
    supportCentreCm: 3, // [E] crust support, zero-anchored; the width stands for the spread of crust thickness
    supportWidthCm: 1.5, // [E] within a unit, so corn gives way to slush over ~6 cm (2 to 3 h) of thaw, not in one step
    giTauH: 36, // [E] tuned round 2: groomed-in freshness decays with exp(-hSinceGroom/36); with the contract 18 h, snow groomed in at 20:00 had lost half its velvet before the lifts opened (A6 pre 86 to 93 read 85); was 18
    freshPasses: 750, // [E] tuned round 6: freshness exp(-(n/750)^2); round 1 set exp(-n/450) (contract exp(-n/300)); corduroy lasts into the late morning on an ordinary day
    freshPassesExponent: 2, // [E] tuned round 6: new; corduroy wears off as exp(-(n/freshPasses)^2), so the first hundred passes of a quiet morning barely mark it and a busy day still skis it off by late morning (P1 85 to 92 with P13 and A7); 1 reproduces the round 1 exponential
    freshAgeCentreH: 36, // [E] corduroy not re-groomed for ~1.5 days is no longer fresh
    freshAgeWidthH: 6, // [E]
    firmFrozen: 0.2, // [E] a refrozen crust adds firmness
    firmWind: 0.35, // [E] tuned round 2: wind packing of natural snow, 0.35 windIdx^3; a day of 60 km/h wind (index near 1) leaves hard wind board and sastrugi, not buff (T4 20 to 58 read 65), while lightly wind-touched snow stays soft (cold-start gap in the chained test); was 0.25 windIdx
    firmWindExponent: 3, // [E] tuned round 2: new; convexity of wind packing in the wind index (natural snow only)
    firmWindPiste: 0.25, // [E] tuned round 2: new name; the piste branch keeps the contract's linear 0.25 windIdx (was firmWind)
    firmWetLoss: 0.6, // [E] wet snow is soft whatever its history (stops scrape building on slush)
    hardpackFirmCentre: 0.8, // [E] contract 0.65 made cold two-day-old groomers "hardpack"; packed powder stays soft packed
    hardpackFirmWidth: 0.06, // [E]
    scrapeFirmCentre: 0.82, // [E] tuned round 1: scraping needs old, dense, cold, well-trafficked snow (firmness before the scrape term); rescaled with the round 1 piste firmness (was 0.9)
    scrapeFirmWidth: 0.04, // [E] contract sigma((firm - 0.6)/0.08) on firmness including scrape ran away
    scrapeFreshShield: 0.8, // [E] fresh corduroy slows scraping by up to 80 %
    giPasses: 1200, // [E] tuned round 3: groomed-in fresh snow stays velvet for more of a quiet day (P2 88 to 95); round 2 set 900, was 600
    compactRhoMax: 450, // [E] traffic compaction conserves new-snow mass up to this density
    slopeFactorMax: 4, // [E] cap on (tan slope / tan 20)^2
    chopMassRhoRef: 120, // [E] chop keeps its new-snow mass after compaction: depth x rho / 120 as equivalent cm
    heroRhoCentre: 140, // contract hero lightness sigma((140 - hnRho)/25)
    heroRhoWidth: 25,
    slushBaseGCentre: 0.9, // contract: supportive slush needs G >= 0.9
    slushBaseGWidth: 0.03, // [E]
    slushRottenWetMemMm: 30, // [E] tuned round 2: new; recent melt or rain (36 h wet memory, mm) beyond which slush is rotten and no longer supportive (south aspects by early afternoon in April, north ones seldom)
    slushRottenWidthMm: 4, // [E] tuned round 2: new; logistic width
    noonResetHour: 12, // [E] hnOvernight restarts at noon (daytime snow is groomed in by the evening pass)
    diurnal: { opening: 0.6, lateMorning: 1.2, lunch: 0.8, afternoon: 1.1, late: 0.7 }, // contract Section 10 step 8 [E]
    hardpackEpsilon: 1e-6, // contract: keeps sum of affinities positive
    maxStates: 6, // states returned, sorted by probability
    minStateP: 0.005,
    // Moved from bare constants in surface.ts (consolidation, 26 Sep).
    diurnalFromHour: { lateMorning: 10, lunch: 12, afternoon: 13, late: 15 }, // contract Section 10 step 8: local hour each diurnal label starts [E]
    firmBase: 0.2, // contract step 9: firmness = 0.2 + 0.3 (rho - 400)/200 + 0.25 min(1, -Ts/10) + 0.2 sinter + 0.15 min(1, age/7 d) [E]
    firmRhoGain: 0.3, // [E]
    firmRhoRef: 400, // [E] kg/m3
    firmRhoSpan: 200, // [E] kg/m3
    firmColdGain: 0.25, // [E]
    firmColdSpanC: 10, // [E]
    firmSinterGain: 0.2, // [E] piste only (natural snow has no mechanical sintering)
    firmAgeGain: 0.15, // [E]
    firmAgeSpanD: 7, // [E]
    scrapeFirmGain: 0.4, // contract: + 0.4 scrape in firmness
    // Tuned round 1: piste firmness recalibrated so hardness comes from traffic packing as well as
    // cold, sintering and age. With the contract weights every cold groomer older than a week read
    // hardpack from the first run (firm ~0.92: P1, P6, P14 at 60 to 65 against 80 to 92), and a
    // quiet day could not be told from a busy one (P13, A7). Natural snow keeps the contract weights.
    pisteFirmBase: 0.05, // [E] tuned round 1 (contract 0.2)
    pisteFirmColdGain: 0.15, // [E] tuned round 1 (contract 0.25): cold snow is harder, but cold groomed snow skis as packed powder
    pisteFirmSinterGain: 0.1, // [E] tuned round 1 (contract 0.2)
    pisteFirmAgeGain: 0.1, // [E] tuned round 1 (contract 0.15 over 7 days)
    pisteFirmAgeSpanD: 14, // [E] tuned round 1: age hardens over two weeks (contract 7 days)
    pisteFirmTrafficGain: 0.45, // [E] tuned round 1: new; traffic packing since grooming
    pisteFirmWorkedGain: 0.1, // [E] tuned round 6: new; firmness added by worked snow (surface.ts workedSnow): a long busy dry spell starts each day harder (P13, A7)
    pisteFirmWorkedPasses: 6000, // [E] tuned round 6: new; e-folding passes of worked snow since the last snowfall, across days (about twelve ordinary days or six busy ones)
    workedTextureMax: 0.5, // [E] tuned round 6: new; fully worked natural snow counts as half machine snow in the corduroy and soft-packed made-snow loss (fine rounded grains)
    workedExponent: 3, // [E] tuned round 6: new; worked = (1 - exp(-n/N))^3, convex so a normal week of traffic barely registers
    pisteFirmTrafficPasses: 800, // [E] tuned round 1: new; e-folding passes of traffic packing (about a busy day)
    pisteFirmMadeGain: 0.05, // [E] tuned round 3: machine snow's firm, chalky character now acts through madeCorduroyLoss rather than firmness, so concentrated early-season traffic no longer polishes a machine base by the afternoon (P3 above 60 with P4 below 72); round 1 set 0.2 (new)
    madeCorduroyLoss: 20, // [E] tuned round 6: machine-snow corduroy after the slower corduroy wear and the grooming-order fix (P4 at or below 72); was 17.5. Tuned round 3: corduroy and soft packed on an all-machine surface score up to 17.5 lower (rubric chalky soft packed and grippy hardpack, 62 to 72: P4 at or below 72 once the maintenance prior and firmness change raised it); round 2 set 13.5, was 8 (round 1, new)
    pisteFirmMadeFrom: 0.5, // [E] tuned round 1: new; made share of SWE from which made snow reaches the surface (smoothstep to all made)
    drainIceCentre: 0.5, // [E] ice index at which ice layers impede drainage by half (3x slower when icy)
    drainIceWidth: 0.1, // [E]
    wetThetaCentrePct: 1.5, // [E] wet indicator (theta > ~1.5 %), zero-anchored logistic
    wetThetaWidthPct: 0.75, // [E] tuned round 1: smoothness invariant (0.5 C moves melt onset by ~15 min; with width 0.5 the dry-to-wet switch flipped the whole state mix within that time); was 0.5
    rainingLowMm: 0.05, // [E] rain falling now: smoothstep over 0.05..0.35 mm/h (contract centre 0.2 mm/h, zero with no rain)
    rainingSpanMm: 0.3, // [E]
    rainShareEpsMm: 0.05, // [E] tuned round 1: new; pseudo-count (mm/h) in the liquid share of precipitation that weights the rain-soaked state
    frozenCrustCentreCm: 1.5, // [E] refrozen crust at the surface of dry snow, zero-anchored logistic
    frozenCrustWidthCm: 0.5, // [E]
    veryWetThetaPct: 9, // [E] contract state table: very wet sigma((theta - 9)/1.5)
    veryWetWidthPct: 1.5, // [E]
    lowThetaPct: 8, // [E] contract state table: low liquid sigma((8 - theta)/1.5)
    lowThetaWidthPct: 1.5, // [E]
    newSnowCentreCm: 2, // [E] contract state table: new snow sigma((hn - 2)/0.8), zero-anchored
    newSnowWidthCm: 0.8, // [E]
    cornCrustCentreCm: 2, // [E] corn score rises with a supporting crust sigma((zf - 2)/1)
    cornCrustWidthCm: 1, // [E]
    pisteCornWetMemMm: 10, // [E] tuned round 2: new; piste corn needs about 10 mm of recent melt (36 h wet memory); less is the early moist phase (cold-sun corn on winter groomers at -5 to -9 C, A7)
    cornThetaPct: 6, // [E] and falls with liquid sigma((6 - theta)/1.5)
    cornThetaWidthPct: 1.5, // [E]
    moistThetaPct: 5, // [E] moist score sigma((5 - theta)/1.5)
    moistThetaWidthPct: 1.5, // [E]
    slushWetThetaPct: 14, // [E] slush score sigma((14 - theta)/2)
    slushWetWidthPct: 2, // [E]
    groomedInCentreCm: 2, // [E] groomed-in snow counts from about 2 cm, zero-anchored logistic
    groomedInWidthCm: 1, // [E]
    heroColdCentreC: 1, // [E] hero score cold term sigma((-Ts - 1)/1.5)
    heroColdWidthC: 1.5, // [E]
    heroWarmLoss: 20, // [E] tuned round 3: new; fresh snow on a groomer at a surface near 0 C is heavy and sticky (rubric: cut-up heavy snow 50 to 62, dense new snow 52 to 66), so its score drops by up to 20
    heroWarmCentreC: -1, // [E] tuned round 3: new; surface temperature at which half of heroWarmLoss applies
    heroWarmWidthC: 0.5, // [E] tuned round 3: new; logistic width
    velvetPasses: 600, // [E] tuned round 3: velvet score decays with exp(-nPass/600): groomed-in snow keeps its velvet through a quiet morning (P2); was 400
    corduroyIdealTsC: -12, // [E] tuned round 1: cold corduroy is the best corduroy (rubric 80 to 88 'cold corduroy'); peak at -12 C, was -6
    corduroyTsWidthC: 20, // [E] tuned round 2: a -24 C night-cooled skin (colder than the groomed layer it stands for) still scores about 88 at [82, 8]; round 1 set 14, was 6
    hardpackScoreFirmCentre: 0.88, // [E] hardpack score falls with firmness 1 - sigma((firm - 0.88)/0.04)
    hardpackScoreFirmWidth: 0.04, // [E]
    tilledPasses: 300, // [E] tilled granular score decays with exp(-nPass/300)
    polishedFirmCentre: 0.75, // [E] polished needs firmness sigma((firm - 0.75)/0.05)
    polishedFirmWidth: 0.05, // [E]
    steepIceSlopeCentreDeg: 30, // contract: steep ice on Pg slopes over 30 deg, sigma((slope - 30)/3) [E]
    steepIceSlopeWidthDeg: 3, // [E]
    rainSoakedSpanFactor: 0.5, // [E] rain-soaked score uses half the anchor span (piste: scaled by dryness)
    chopSoftCentreCm: 3, // [E] traffic chops soft snow: new snow zsig((hn - 3)/2) or liquid theta/6
    chopSoftWidthCm: 2, // [E]
    chopSoftThetaPct: 6, // [E]
    chopWetPasses: 150, // [E] tuned round 1: new; wet snow ruts on the contract's ~10^2 pass scale (the dry new-snow scale is chopPasses)
    chopSupportShield: 0.65, // [E] tuned round 1: new; a supportive crust under wet snow slows traffic chop by up to 65 % (corn skis well tracked until it turns to slush: R2, A8)
    slopeFactorRefDeg: 20, // [E] scrape slope factor (tan slope / tan 20)^2
    fallbackQ: 0.62, // [E] hardpack anchor, used only if inputs are not finite
    /** Initial surface: settled, dry, groomed yesterday. Fields not listed start at 0. [E] */
    initial: {
      Ts: -2, // [E] C
      fdh: 49, // steady-state memory for Ts = -2
      hnRho: 100, // [E] kg/m3
      hoursSinceSnow: 72, // [E]
      hSinceGroom: 24, // [E]
      sinter: 1, // [E] fully sintered
      firm: 0.5, // [E]
      untracked: 1, // [E]
    },
  },
  /** Piste rubric anchors [base, span] from the contract table, anchored to the Section 5.10 rubric of the deep dive. [E] */
  pisteScore: {
    rainSoaked: [25, 10],
    hero: [88, 12],
    velvet: [87, 7], // tuned round 3: groomed-in fresh peaks at 94, 1 above the rubric 86 to 93, like corduroy in round 2 (P2 88 to 95 is a whole-day band); was [86, 7]
    corduroy: [82, 8], // tuned round 2: cold corduroy peaks at 90, 2 above the rubric 80 to 88, because the midwinter-perfect day (P1 85 to 92) cannot average 85 when its best state tops out at 88 (see the report); was [80, 8]
    corn: [80, 8],
    softPacked: [70, 10],
    softMoguls: [65, 13],
    hardpack: [62, 10],
    tilled: [50, 12],
    chopDry: [50, 12],
    chopLight: [68, 12], // tuned round 1: new; cut-up light cold snow on a groomer (rubric soft packed with small piles 70 to 80)
    chopWet: [40, 10],
    moist: [50, 12],
    slushSupported: [40, 15],
    slushIsothermal: [15, 15],
    polished: [40, 15],
    ice: [25, 13],
    frozenChop: [12, 16],
    wind: [45, 15],
    steepIce: [10, 15],
  },
  offpiste: {
    trackFastH: 2.5, // [E] tuned round 6: the best quarter is now taken by area, untracked area first (engine.ts), so the easily reached lines can track out within a morning as they do; round 1 set 10 to hold the unit-mean untracked share up; contract 4
    trackSlowH: 35, // [E] tuned round 6: the remote fifth of the terrain stays untracked for days (O3 day 3 50 to 62); round 1 set 40, contract 16
    trackFastShare: 0.82, // [E] tuned round 6: 82 % of lift-served off-piste is the easily reached, quickly tracked terrain; with the area best quarter the day after a storm is partly tracked (O2 66 to 80); was 0.6
    sidecountryTrackFactor: 3, // [E]
    refillMm: 8, // [E]
    powderRefRho: 68, // [E] tuned round 6: the Hedstrom-Pomeroy cold new-snow density (freshRhoBase 67.9 [S]), so 25 mm of cold snow reads as the 35 cm it fell as (O1 30 cm+ 95 to 100); round 1 set 80
    powderDepthSoftCm: 10, // [E] review fix 27 Sep: new; the untracked powder score reaches the 76 + 24 phi anchor at 10 cm fallen on a soft base (rubric 76 to 86 at 10 to 20 cm)...
    powderDepthHardCm: 20, // [E] ...and at 20 cm on a hard base; below it blends from the settled-snow score of the base (3 to 7 cm on a ten-day-old base read 67 to 78 "Firing", rubric 52 to 66). The base's hardness and settled score use its age before the new snow reset it
    maxBaseAgeD: 60, // [E] review fix 27 Sep: new; cap on the inferred age of the base under new snow (days)
    powderF50SoftCm: 12, // [S] expert guidance
    powderF50HardCm: 22, // [S]
    recycledNightC: -12, // [S] Birkeland
    recycledDayC: -5, // [S]
    recycledHours: 36, // [S]
    // Moved from offpiste.ts OFFPISTE_LOCAL_PARAMS (consolidation, 26 Sep). [E] unless tagged [S].
    maxTrackHours: 400, // numerical cap on lift-hours since snowfall
    recycledDayWeightLowDeg: -2, // [E] day threshold blends in with sun elevation -2..6 deg
    recycledDayWeightSpanDeg: 8,
    recycledColdWidthC: 1, // [E] width of the recycled-powder cold condition
    recycledShadeWm2: 150, // [E] absorbed shortwave that stops near-surface faceting (shaded slopes build)
    recycledColdDecayH: 12, // [E] decay time when no longer cold
    recycledWetDecayH: 3, // [E] decay time when wet
    recycledSunDecayH: 3, // [E] decay time at full sun (absorbed 200 W/m2): sun crusts it within hours
    recycledSunWm2: 200, // [E]
    recycledBuryCm: 3, // [E] new snow buries recycled powder
    hardBaseRhoCentre: 380, // [E] a dense old base counts as hard for powder quality
    hardBaseRhoWidth: 15, // [E] tuned round 6: a settled winter base of 300 to 330 kg/m3 is not a hard base (weight 0.01, was 0.07 at width 25); only dense old snow near 380 is (O1 30 cm+ 95 to 100); was 25
    hardBaseCrustWidthCm: 0.3, // [E] a crust (zf > 1) makes the base hard
    hardBaseAgeD: 7, // contract: snowAgeD < 7 for a soft base
    hardBaseAgeWidthD: 1, // [E]
    powderLightRhoCentre: 175, // [E] contract sigma((120 - rho)/25) recalibrated: settled day-old powder (~150) stays light; tuned round 6: was 170
    powderLightRhoWidth: 12, // [E] tuned round 6: cold smoke settled overnight (100 to 120 kg/m3) reads fully light (0.99, was 0.97 at width 20) and snow set up past 200 still loses its lightness; was 20
    powderDenseRhoCentre: 220, // [E] dense, set-up new snow drops towards 52..66
    powderDenseRhoWidth: 20,
    powderDenseLoss: 16,
    crustThinCm: 10, // contract dust-on-crust upper depth; thin new snow on a crust is not powder
    crustThinWidthCm: 2,
    touringDeepCm: 20, // [E] tuned round 1: contract hn >= 25 cm refers to the fallen depth; the store's settled hn is about 20 % lower by the descent (T3); was 25
    touringDeepWidthCm: 2, // [E]
    touringLightRho: 120, // [E] tuned round 1: cold new snow settles past 90 kg/m3 within hours of falling (contract freshRho < 90 at fall); was 90
    touringLightWidth: 6, // [E]
    windwardCentre: 0.3, // matches snowpack windward cos 0.3, ramp 0.4
    windwardSpan: 0.4,
    windBoardDepositMm: 15, // contract sigma((windDepositMm - 15)/5)
    windBoardDepositWidthMm: 5,
    chunderCrustCm: 3, // [E] a thick old crust reads as refrozen after its wet memory fades
    chunderCrustWidthCm: 1,
    settledFloor: 0.01, // [E] keeps the affinity sum away from zero
    // Moved from bare constants in offpiste.ts (consolidation, 26 Sep).
    hardBaseCrustCm: 1, // contract: a crust zf > 1 cm makes the base hard (soft base: zf < 1 and age < 7 d)
    crudDecayH: 24, // [E] set-up crud score decays with exp(-hoursSinceSnow/24)
    windBuffWindwardLoss: 0.7, // [E] wind buff is mostly absent on windward slopes (1 - 0.7 windward)
    windHardFirmCentre: 0.6, // [E] wind-affected snow reads hard from firmness sigma((firm - 0.6)/0.08)
    windHardFirmWidth: 0.08, // [E]
    breakableMinCm: 0.5, // [E] breakable crust: zf above about 0.5 cm, zero-anchored logistic
    breakableMinWidthCm: 0.3, // [E]
    breakableMaxCm: 3, // [E] and below about 3 cm (thicker crusts support)
    breakableMaxWidthCm: 0.7, // [E]
    breakableAgeD: 5, // [E] on young snow (snow age below about 5 days)
    breakableAgeWidthD: 2, // [E]
    dustMinCm: 1, // [E] dust on crust needs about 1 cm of new snow, zero-anchored logistic
    dustMinWidthCm: 0.5, // [E]
    chunderWetMemMm: 10, // [E] refrozen chunder weight from wet memory, clamp(wetMem/10)
    cornWetMemMm: 10, // [E] tuned round 4: corn grains need about 10 mm of recent melt (36 h wet memory), as on piste (pisteCornWetMemMm); at 5 a high north face with one weak melt cycle read as ripe corn from noon to 15:00 and beat the classic east-then-south descent (T1 window); less is moist, sun-affected snow; round 1 set 5 (new)
    chunderIceLoss: 14, // [E] tuned round 1: new; an icy rain crust scores down to 8 (rubric breakable crust 5 to 18)
    settledAgeD: 10, // [E] settled score decays with exp(-snowAgeD/10)
  },
  /** Off-piste rubric anchors [base, span] (contract Section 11 table, deep dive Section 5.10); touring anchors from contract Section 12. [E] */
  offPisteScore: {
    rainSoaked: [10, 10],
    powderUntracked: [76, 24],
    setUpCrud: [40, 24], // tuned round 2: freshly cut-up snow is soft chop (rubric 52 to 66, 64 here) and sets up into crud over about a day (rubric set-up crud 38 to 52); [52, 14] never fell below the soft-chop band (O3 day 7, O10); was [52, 14]
    stickyNew: [40, 15],
    windBuff: [66, 10],
    windBoard: [35, 15],
    sastrugi: [22, 16],
    corn: [76, 10],
    overSoft: [38, 14],
    isothermalSlush: [5, 13],
    moist: [44, 8],
    breakableCrust: [5, 13],
    dustOnCrust: [22, 16],
    refrozenChunder: [22, 16],
    recycled: [76, 10],
    settled: [45, 17],
    touringCorn: [88, 8],
    touringDeepPowder: [92, 8],
  },
  touring: {
    ascentMPerH: 375, // [E]
    transitionH: 0.5,
    startEarliestFromSunriseH: -1.5,
    startLatest: 11,
    startStepH: 0.5,
    carryScaleM: 400, // [E]
    trailBreakCentreCm: 35, // [E]
    trailBreakWidthCm: 8,
    icyTraversePenalty: 0.05, // [E] at most 5%
    weightDescent: 0.7, // [E] research rubric suggestion
    weightAscent: 0.15,
    weightFeasibility: 0.15,
    descentWeatherExponent: 1, // [E] tuned round 8: new; the descent is skied in the weather W at its descent time, which multiplies the plan value linearly as it does every off-piste unit score (100 G q W). F keeps the contract's summit W as feasibility (inside F^0.15). With W only inside F^0.15 a whiteout storm (W 0.25) cost touring 19 % against 75 % for piste and off-piste, and single-band touring at the rain and snow line moved 15 to 17 points for 0.5 C (smoothness invariant); was 0 (contract)
    wetNightCentreC: -2.5, // [E] tuned round 1: a surface that only reaches -1 to -2 C at night has not refrozen (T2); was -1
    wetNightWidthC: 1,
    wetCloudCentrePct: 60, // [E] tuned round 1: was 70
    wetCloudWidthPct: 10,
    wetEnergyCentreMJ: 1.2, // [S] Mitterer & Schweizer 2013, to recalibrate
    wetEnergyWidthMJ: 0.7, // [E] tuned round 4: the 3-day wet energy (E3) moves by about 0.25 MJ for 5 % more cloud or 0.5 C (longwave and sensible heat over a melt day), so at the contract's 0.3 MJ the wet-snow ceiling (82 points per unit w) jumped 5 or more points (smoothness invariant); w2 is zero-anchored at E3 = 0; was 0.3
    ceilingSpan: 82, // [E] tuned round 3: Tour <= 100 - 82 w, so a fully wet day tops out at 18, the rubric's isothermal slush top (5 to 18); with 80 it sat exactly on the T2 band edge (20); contract 80
    shaveFraction: 0.3, // [E]
    shaveMax: 5, // [E]
    // Moved from touring.ts TOURING_LOCAL_PARAMS (consolidation, 26 Sep); contract values are marked as such.
    trailBreakMaxLoss: 0.4, // [E] contract Section 12: deep trail breaking costs at most 40 % of A
    icyFirmCentre: 0.8, // [E] contract: sigma((firm_dawn - 0.8)/0.05)
    icyFirmWidth: 0.05, // [E] contract
    icySlopeCentreDeg: 30, // [E] contract: icy traverse only on sustained slopes over 30 deg
    icySlopeWidthDeg: 3, // [E] contract
    descentMPerH: 1500, // [E] touring party descent rate including regroups; sets t_end
    feasibilityFloor: 0.05, // [E] contract: F = max(0.05, ...)
    wetMarginWidthH: 1, // [E] contract: sigma((t_wet - t_end)/1 h)
    wetLevel: 0.5, // [E] contract: t_wet is when w reaches 0.5 (the median passage time since round 7; used by wetOnsetHour for explanations)
    // tuned round 7: wetHazardPerH (6) and wetHazardWidth (0.02) removed; the wet passage time is
    // distributed as the running maximum of w (touring.ts buildWetMargin), so F no longer steps.
    wetPassageSteps: 144, // numerical: integration steps from the earliest start to midnight (was wetHazardSteps)
    snowlineLevelG: 0.5, // [E] snowline where skiability G falls below 0.5
    skiedShareRamp: 0.2, // [E] review fix 27 Sep: new; a band's touring cover floor moves from 1 (not skied, wholly below the snowline) to lerp(G at its top, 0.5, share) as its skied share rises through 0.2 (it was lerp(1, 0.5, share), which read the top sliver of a thin band as full cover)
    skiableVerticalM: 100, // [E] review fix 27 Sep: new; the touring descent's cover G is scaled by smoothstep(G-weighted vertical of the descent / 100 m), so a tour with nothing to ski scores about 0 (the per-band floor lerp(1, 0.5, above) read a bare mountain as full cover: touring 47 Fair with no snow)
    snowlineWidthG: 0.05, // [E] logistic width in G for the smooth bare-height integral
    defaultSunriseHour: 7, // [E] fallback when sunrise is unknown
    dayEndHour: 24, // plans and passage times are evaluated within the local day
    ceilingTop: 100, // contract: ceiling 100 - 80 w
  },
  weather: {
    floor: 0.25, // [E]
    missingDayW: 0.8, // [E] review fix 27 Sep: new; weather factor of an hour on a day with no weather at all (a missing hour otherwise takes W from its nearest valid hours); it was 1, so a day without weather outscored a bluebird day
    visCentreM: 1100, // [E] review fix 27 Sep: 2 km visibility now costs about 31 % of W (was 53 % at the contract's 2500 m centre) and 200 m still gives 0.12; moderate snowfall with 1 to 2 km visibility halved every score and read a calm, snowing fresh-snow day as Challenging; P10, A1 and A2 stay in band; 1100 rather than 1000 m keeps a rain-snow transition inside the 0.5 C smoothness limit (the larger W amplified it to 5.01); contract 2500
    visSlope: 1.2,
    visClearRefM: 20000, // [E] tuned round 2: new; visibility quality is normalised to 1 at and beyond clear-air visibility (the contract logistic gave 0.97 at 50 km, so a bluebird day lost 3 % in W and piste tops (P1, P6, A6) could not reach their bands)
    immersedVisM: 200, // [E]
    flatContrastLoss: 0.3, // [E]
    flatDiffuseLow: 0.55, // [E]
    flatDiffuseSpan: 0.4,
    treeRelief: 0.6, // [E]
    windCentreKmh: 50, // [E]
    windWidthKmh: 9,
    windFloor: 0.3,
    liftHoldGustKmh: 70, // [E]
    liftHoldWidthKmh: 8,
    coldStartC: -15, // [E] tuned round 1: a -12 C bluebird powder morning is not a cold-comfort day (O1 95 to 100 was capped near 87 by Cm 0.92); was -10
    coldFloorC: -25, // [E] tuned round 1: was -20
    coldFloor: 0.8,
    rainLossMax: 0.25, // [E]
    rainFullMm: 2,
    freezingRainFactor: 0.5, // [E]
    // Moved from weather-factor.ts (consolidation, 26 Sep).
    windSustainedWeight: 0.6, // [E] wind factor blend of sustained and gust speed (contract Section 13)
    windGustWeight: 0.4, // [E]
    gustFactorFallback: 1.5, // [E] gust = factor x mean wind when the gust field is missing (typical mountain gust factor)
    visMinM: 50, // visibility floor inside the log (contract Section 13: ln max(vis, 50))
    sunUpRampDeg: 2, // [E] flat light applies "whenever the sun is up": weight ramps in over 0 to 2 degrees of sun elevation
    diffuseClearSky: 0.15, // [E] diffuse fraction under a clear sky, for the cloud proxy when radiation is missing
    cloudDefaultPct: 50, // [E] cloud cover used when neither cloud nor a coded sky state is available
    /**
     * [E] Clear-day reanalysis fix (from lib/conditions/piste-scoring.ts): Open-Meteo visibility at
     * mountain elevations can read unrealistically low on clear days, so when the weather code
     * itself reports a clear or partly cloudy sky (WMO 0 to this code) the code estimate floors the
     * measured value. The old gate on low cloud and direct sun is replaced by this categorical test
     * so no continuous cloud threshold is added; units inside cloud are handled by immersion instead.
     */
    visFloorMaxClearCode: 2,
    /**
     * [E] Freezing rain applies in proportion to the liquid share of this unit's precipitation,
     * (rain + k)/(precip + k), so a fetch-point freezing-rain code does not penalise a unit where it
     * is snowing. k keeps the share smooth and lets a coded freezing rain with no amounts still count in full.
     */
    freezingRainPseudoMm: 0.1,
    codeVisibilityM: WEATHER_CODE_VISIBILITY_M, // [E] WMO code visibility table (see above)
    codeVisFallbackM: 50000, // [E] codes outside the table: visibility 50000 exp(-2 cloud/100) (existing behaviour)
    codeVisFallbackCloudDecay: 2, // [E]
    /** [E] Rough sky cover (percent) from a weather code, used only when cloud and radiation are both missing. */
    codeCloudPct: { clear: 0, mainlyClear: 25, partlyCloudy: 50, overcast: 100 },
  },
  aggregate: {
    clusterSoftMaxBeta: 0.05, // [E]
    memberQuantileWindow: 0.1, // [E] tuned round 4: new; half-width (in cumulative member weight) of the window over which member P10, P50 and P90 average the order-statistic quantile, so they are continuous when two members of unequal weight swap order (smoothness and saturation invariants); 0.1 keeps P50 on the central member of a monotone ensemble
    mandatoryWeight: 0.2, // [E] tuned round 2: a mandatory home run is skied at least twice a day (lunch or arrival, and the end of the day) out of about ten runs; at 0.12 a bare mandatory home run moved the late-April ribbon by 5 points at most (A5b 45 to 60); was 0.12
    unmappedHomeRunShare: 0.5, // [E] tuned round 6: new; without mapped runs the lowest band holds the way home, skied at the end of the day unless a lift downloads; half of resorts are assumed to have no download, so the base band enters the resort mean with mandatoryWeight x 0.5 (late April with a bare base, P12 45 to 60, against the mapped ribbon with a download lift, A5a 63 to 70)
    runSoftMinP: 6, // [E]
    pisteBestWeight: 0.3, // existing
    pisteWindowH: 4,
    offPisteBestWeight: 0.5,
    offPisteWindowH: 3,
    offPisteTopShare: 0.25, // [E]
    extentNoPenaltyShut: 0.25, // owner decision 2026-09-26
    extentFullPenaltyShut: 0.75, // owner decision
    extentMaxPenalty: 0.25, // owner decision
    extentKmSaturation: 60, // [E]
    extentPriorShrink: 0.5, // [E]
    observedExtentMinCoverage: 0.6, // [E]
    closedOperatingProbability: 0.2, // [E]
    openOperatingProbability: 0.8,
    coverRiskG: 0.5,
    coverRiskProbability: 0.2,
    iceRiskProbability: 0.3,
    // Moved from aggregate.ts AGGREGATE_LOCAL_PARAMS (consolidation, 26 Sep).
    /**
     * Pseudo-count of the unconditional class mixture added to class-conditional quality, so
     * q tends smoothly to the unconditional expectation as the open probability tends to 0.
     * [E] numerical regulariser, five orders below the smallest modelled open probability.
     */
    openPseudoCount: 1e-9,
    /**
     * Pseudo open probability added to each cluster's soft-max weight, so a band whose
     * clusters are all shut keeps a finite length-and-traffic weighted "if open" quality.
     * [E] numerical regulariser.
     */
    weightPseudoOpen: 1e-9,
    /**
     * Half width of the smooth switch from modelled to observed extent around the 60 %
     * coverage threshold (observedExtentMinCoverage): the blend runs from 0.5 to 0.7. [E]
     */
    observedBlendHalfWidth: 0.1,
    /** Floating-point tolerance for probability thresholds and window ties. [E] numerical */
    tolerance: 1e-9,
    /**
     * Natural vocabulary on the 0 to 10 scale, highest first. Thresholds copied from the
     * offPisteV5Label vocabulary so stored labels do not change meaning. [E] legacy vocabulary
     */
    naturalLabels: [
      [9.0, "Hero"],
      [7.5, "Firing"],
      [6.5, "Great"],
      [5.0, "Good"],
      [3.5, "Fair"],
      [2.0, "Poor"],
    ] as readonly (readonly [number, string])[],
    /** Natural vocabulary word below the lowest threshold. [E] legacy vocabulary */
    naturalFloorLabel: "Flat",
  },
  evidence: {
    runOpenCentreC: 0.8, // [E]
    runOpenCentreLateC: 0.7, // [E] last 21 days of season
    runOpenWidthC: 0.07, // [E]
    runOpenEpsilon: 0.009, // [E] tuned round 3: 0.9 % of runs are open whatever the modelled cover (model error, snow farming, patched runs); with 0.005 a bare unequipped run open at 0.5 % made the rare equipped class dominate a nearly shut resort's conditional quality (P5a 0 to 15); kept below 0.0095 so an open report still favours good cover 100 to 1; contract 0.005
    runOpenCeiling: 0.97,
    closedCredible: 0.9, // [E]
    closedWeak: 0.3, // [E]
    closedBenignGustKmh: 60,
    closedBenignNewSnowCm: 20,
    closedBenignOpenShare: 0.3,
    repeatTemper: 0.2, // [E]
    resortCoverTemper: 0.5, // [E]
    stationSigmaBase: 0.1, // [E]
    stationSigmaElevation: 0.25,
    stationSigmaElevationScaleM: 400,
    stationSigmaPerKm: 0.01,
    stationRepresentativenessScaleM: 500, // [E] review fix 27 Sep: new; a station depth updates a unit with weight exp(-|dz|/500 m) (model depths use 800 m); a valley station reading 0 m in spring wiped the 3,000 m bands (contract amendment requested)
    stationDepthGradientPerM: 0.0008, // [E] review fix 27 Sep: new; the station depth is mapped to the unit elevation at 8 cm more snow per 100 m of height before comparison (typical Alpine winter gradients 5 to 15 cm per 100 m)
    stationMaxKm: 60,
    stationAgeTauH: 72, // [E]
    nwpOffsetM: 0.05,
    nwpElevationSigmaPerKm: 0.5, // [E]
    nwpRepresentativenessScaleM: 800, // [E]
    depthSpreadFloor: 0.2, // [E] tuned round 1: floor on the ensemble spread of ln(depth + 0.05) in the log-space depth update (model error between observations), so repeated model or station depths keep pulling the ensemble towards them
    evidenceSpreadH: 4, // [E] tuned round 3: new; hours over which a natural observation (model depth, station, satellite, check-in) is applied in equal tempered shares (incremental analysis update); a one-hour availability shift then moves any hour by a quarter of the update at most
    satelliteKappa: 8, // [E]
    satelliteMiss: 0.2, // [S] recall 78%
    satelliteMinPixels: 5,
    satellitePresenceSweKgM2: 9, // [E] ~3 cm at 300 kg/m3
    satellitePresenceSpread: 0.5, // [E]
    checkinDiagonal: 0.7, // [E]
    checkinAdjacent: 0.12, // [E]
    operatingMidSeason: 0.95, // [E]
    operatingOutOfSeason: 0.05, // [E]
    operatingRampDays: 14, // [E]
    resortOpenProbability: 0.98, // [E]
    resortClosedProbability: 0.05, // [E]
    allClosedOpenFraction: 0.1, // [E] review fix 27 Sep: new; when operator run statuses cover the mapped length (observed-extent weight) and their open share falls below about 10 % (smoothstep) on a benign day, the operating probability blends to the closed value (every run closed read as an open resort, piste "Good")
    evidenceKeyLimit: 200,
    // Moved from evidence.ts (consolidation, 26 Sep).
    nwpZeroBlendM: 0.01, // [E] model depths blend from the zero branch (0 m) to the positive branch (Open-Meteo's 1 cm step)
    stationTaperFraction: 0.25, // [E] station evidence fades smoothly to 0 over the last quarter of stationMaxKm
    closedGustWidthKmh: 6, // [E] logistic width of the benign-gust test for closures
    closedNewSnowWidthCm: 3, // [E] logistic width of the benign new-snow test
    closedOpenShareWidth: 0.06, // [E] logistic width of the "comparable runs open" test
    closedPeerNeutral: 0.5, // [E] a missing same-band open share is neutral in the closure credibility (factor 0.5)
    resortClosedWindWidthKmh: 6, // [E] closed resort: wind-hold versus benign closure
    resortClosureRampH: 1.5, // [E] review fix 27 Sep: new; a resort day closure is admitted linearly over 1.5 h after the scheduled opening and before closing (a resort-wide closed report at 11:00 is the day's status; run snapshots keep the 4 h ramp)
    closureAdmitRampH: 4, // [E] tuned round 3: new; unexplained run closures are admitted linearly over 4 h after opening and before closing (closureAdmission), replacing the hard off-hours drop; a one-hour shift in observation time changes their weight by at most a quarter; review fix 27 Sep: resort day closures use the same ramp and closureGraceH (the hard 30-minute grace after opening for closed resort snapshots) is removed
    runClosedStatusWeight: 0.2, // [E] review fix 27 Sep: new; a run shows "Closed" when its admitted closed weight (admission x closed share) reaches 0.2 and exceeds its open weight: an unexplained closure from 09:20 (default lift hours), not a snapshot at opening (weight 0.04) or a closure seen minutes before closing after an open report
    runOpenUsability: 0.99, // [E] contract 8.2: fresh "open" report
    runClosedCredibleUsability: 0.02, // [E] contract 8.2: credible closure
    runClosedWeakUsability: 0.3, // [E] contract 8.2: weak closure (wind, avalanche control, storm)
    lateSeasonDays: 21, // [E] contract 8: C* drops to 0.70 in the last 21 days of the season
    lateSeasonRampDays: 7, // [E] smooth ramp centred on the 21-day mark
    operatingUnknownSeason: 0.5, // [E] operating prior when the location has no season dates
    satelliteMuFloor: 0.05, // [E] contract 8: mu = 0.05 + 0.9 C_pres
    satelliteMuSpan: 0.9, // [E]
    satelliteFalseAlarm: 0.07, // [E] from precision 93 % [S]: share of passes reading snow whatever the truth
    satelliteFscMin: 0.01, // [E] keeps the Beta density finite at fsc 0 and 1
    satelliteFscMax: 0.99, // [E]
    checkinBinEdges: [0.97, 0.75, 0.12] as readonly number[], // contract 9 cover words: full, thin, patchy (incl. marginal)
    checkinBinHalfWidths: [0.015, 0.03, 0.03] as readonly number[], // [E] smoothstep half-widths of the bin edges in G
    checkinNewUserReputation: 0.5, // [E] contract 8: check-in temper for a reporter without a reputation (new user)
    checkinUnitDailyTemper: 1, // [E] security fix 27 Sep: the summed check-in temper (reputation x share x keep) one terrain unit takes in one day never exceeds one full observation; when the day's check-ins on a unit ask for more, each is scaled by cap / total, so extra accounts add nothing (checkinUnitScale)
    requantiseFloor: 1e-9, // [E] relative posterior floor so a one-hot posterior never collapses the ensemble
    equippedMin: 0.001, // [E] tuned round 1: equipped probability never becomes certain (0.01 let a bare Pu cluster read as a 1 % equipped one, P5a); was 0.01
    equippedMax: 0.99, // [E]
    // Engine inputs, 27 Sep (new). No existing value changed. The optional-argument operators below
    // default to neutral values; since register .3 the engine passes the new inputs (hop, record weight,
    // SWE, product temper, run-versus-band rule), which act only on records that carry the new fields.
    nwpHopSigmaPerKm: 0.01, // [E] model depth from a cell hopKm away: sigma of ln(depth + 0.05) widens by 0.01 per km in quadrature (a 30 km hop takes the Alps' 0.45 to 0.54), as stations use 0.01 per km (nwpDepthGaussian hopKm)
    stationExcludedSiteKinds: ["ridge", "grid-model", "bulletin"] as readonly string[], // station records never used as depth evidence: wind-scoured ridge heights, model grids, and avalanche-bulletin aspect estimates (avalanche data never enters the engine)
    stationExcludedQuality: ["forecast"] as readonly string[], // provenance markers that are not observations (seNorge forecast days)
    stationQualityTemper: { "snowpack-model": 0.5 } as Readonly<Record<string, number>>, // [E] temper of a station value that is model output (SLF daily snow from SNOWPACK); other flags 1 (stationRecordWeight)
    stationSweOffsetKgM2: 15, // [E] SWE observation space ln(SWE + 15): the depth offset 0.05 m at 300 kg/m3 (stationSweGaussian)
    stationSweSigmaBase: 0.08, // [E] a snow pillow weighs the pack directly: a tighter base sigma than a depth sensor (0.10) before the same elevation and distance terms
    stationDensityMinKgM3: 30, // plausible bulk density range for measured SWE / HS (fresh snow about 50, wet spring pack about 500, firn below 700)
    stationDensityMaxKgM3: 700,
    stationSweNoiseKgM2: 5, // [E] below 5 cm of measured height (no density defined) a pillow SWE counts only up to the densest plausible pack of that height plus 5 kg/m2 of pillow noise (stationSweForUpdate): residual mass on a melted-out pillow never overrides a 0 cm height
    stationAlbedoMinShortwaveWm2: 100, // measured albedo out / in only with at least 100 W/m2 incoming (low sun and dawn ratios are noise)
    satelliteProductRank: { "clms-hrwsi-fsc": 3, "s2-l2a-scl": 2, "gibs-viirs-ndsi": 1 } as Readonly<Record<string, number>>, // same target and acquisition date: only the best-ranked product applies (HR-WSI FSC is derived from the same Sentinel-2 scene as the SCL; VIIRS NDSI is a 500 m fallback)
    satelliteProductTemper: { "clms-hrwsi-fsc": 1, "s2-l2a-scl": 1, "gibs-viirs-ndsi": 0.5 } as Readonly<Record<string, number>>, // [E] likelihood temper per product (satelliteProductTemper): the 500 m VIIRS NDSI mixes snow-free valley pixels into a band
    satelliteUnknownProductTemper: 1, // products outside the table (fixtures, future products) apply at full weight, as before
    satellitePixelSource: { "clms-hrwsi-fsc": "sentinel-2", "s2-l2a-scl": "sentinel-2", "gibs-viirs-ndsi": "viirs" } as Readonly<Record<string, string>>, // run-versus-band rule (satellitePixelSource): products computed from the same scene share a pixel source (HR-WSI FSC and the SCL both come from the Sentinel-2 scene), so a band target of either covers an ungroomed run pass of the other; products outside the table are their own source
    confidence: {
      stationFreshH: 24, // contract 8.3 class A: station <= 24 h old (a measurement: only full-weight records count, so SNOWPACK model rows give class B at most; engine assembleDay, .3)
      stationNearKm: 5, // within 5 km
      stationNearDeltaM: 100, // and 100 m
      checkinWindowH: 3, // >= 2 concordant check-ins within 3 h
      checkinConcordant: 2,
      checkinConcordantMinReputation: 0.59, // [E] security fix 27 Sep: only reputed reporters count towards concordance, at least 3 prior active days (snowCheckinReputation(3) = 0.59); new accounts (0.5) are free to create, so two of them no longer lift a day to class A
      operatorRecentDays: 3, // class B: operator evidence <= 3 days old
      operatorShareA: 0.5, // [E] review fix 27 Sep: class A for piste needs same-day operator status on at least half of the unit's mapped length (weighted by admission)
      stationRegionalKm: 30, // class B: station within 30 km
      satelliteFreshDays: 5, // class B: satellite <= 5 days old
    },
  },
  /** Cover (moved from cover.ts, consolidation 26 Sep). */
  cover: {
    /** Phi^-1(0.97): coverage C = 0.97 at S1 (contract Section 9). [E] */
    coverageZAtS1: 1.881,
    /** Floor applied to section G before the run soft minimum (contract Section 9). [E] */
    runSoftMinFloor: 0.001,
    /** Cover word thresholds on G (contract Section 9). [E] */
    wordFull: 0.97,
    wordThin: 0.75,
    wordMarginal: 0.4,
    wordPatchy: 0.12,
    /** "Ribbon only": band extent below this with high G on the open runs. [E] */
    ribbonMaxExtent: 0.35,
    /** "High G" for the ribbon rule: the open runs read at least "thin in places". [E] */
    ribbonMinG: 0.75,
  },
  terrain: {
    fiveBandReliefM: 700,
    sectionMaxDropM: 100,
    aspectBreakDeg: 45,
    maxSectionsPerRun: 8,
    maxClusters: 12,
    maxUnits: 72,
    defaultPisteSlopeDeg: 20, // [E]
    offPisteSlopeDeg: 32, // [E]
    steepRougherFromDeg: 30, // [E] ramp 30 to 40 deg
    steepRougherSpanDeg: 10,
    trafficMin: 0.3,
    trafficMax: 3,
    returnRunFlowShare: 0.2, // [E] tuned round 4: new; share of a base lift's capacity that laps the return-to-base runs it serves: the lift runs full for about 1.5 h of access in a 7 h day and each skier skis home about once, so a home run no longer carries three times the median traffic (A5a)
    returnRunDownloadShare: 0.5, // [E] tuned round 4: new; share of skiers who ride a downloadable base lift down instead of skiing home
    concentrationMax: 3, // [E]
    liftUtilisation: 0.6, // [E] tuned round 4: new; typical mean utilisation of an open lift over the day, which the traffic base rate assumes; with lift-derived traffic, skiers displaced from closed terrain cannot push a run beyond its lifts' capacity, so concentration is capped at 1/0.6 (three open glacier runs, A4); without lift data the contract's cap of 3 applies
    strategicSlope: 1.2, // [E]
    equippedTagYes: 0.95, // [E]
    equippedTagNo: 0.002, // [E] tuned round 1: an explicit snowmaking=no tag is a deliberate statement; at 0.05 the class-conditional cluster quality stayed Pe-dominated on bare unequipped runs (P5a); was 0.05
    equippedCannon: 0.9, // [E]
    highAltitudeRankM: 2800, // [E]
    treedShareExtensive: 0.5, // [E]
    treedShareLimited: 0.2, // [E]
    defaultBandWeights3: [0.2, 0.5, 0.3] as readonly number[], // [E]
    defaultBandWeights5: [0.12, 0.2, 0.3, 0.23, 0.15] as readonly number[], // [E]
    // Moved from terrain.ts (consolidation, 26 Sep).
    strategicRankCentre: 2.5, // [E] contract Section 4: equipped prior sigma(a + 1.2 (2.5 - rank))
    fallbackCentreElevationM: 1500, // [E] centre of the assumed relief when the location has neither elevations nor a fetch point
    minBandThicknessM: 30, // [E] quantile edges never make a band thinner than this
    pointHalfWidthM: 1, // [E] zero-drop sections spread over +-1 m in the terrain distribution
    defaultRunLengthM: 100, // [E] run length when neither length nor vertical drop is known
    derivedSlopeMinDeg: 3, // [E] bounds for a slope derived from drop / horizontal length
    derivedSlopeMaxDeg: 45, // [E]
    fallbackReliefM: 600, // [E] relief assumed when neither the location nor runs give one
    minReliefM: 60, // [E] a degenerate elevation range is widened to at least this
    unknownAspect: "E" as AspectSector, // [E] section sector when aspect is unknown (aspectDeg stays null)
    downloadBaseToleranceM: 100, // contract: downloadable lift bottom within 100 m of the base
    rankHighAltWidthM: 50, // [E] logistic width of the 2,800 m rank-4 switch
    rankLowBandWidthM: 25, // [E] logistic width of the lowest-band rank-2 switch
    shareMin: 0.005, // [E] national share clamp for the bisection
    shareMax: 0.995,
    bisectionIterations: 60,
    bisectionBound: 30, // |a| bracket for the strategic-rank offset
    treelineUnknownFraction: 0.3, // [E] contract: treeline = summit - 0.3 relief when unknown
    treelineLimitedFraction: 0.25, // [E] "limited" tree terrain: marginal forest near the base
    mergeAspectAdjacent: 1, // [E] merge distance for a 90 deg aspect difference
    mergeAspectOpposite: 2, // [E] 180 deg (sunny into shady is the worst aspect merge)
    mergeGrooming: 1.5, // [E] groomed vs ungroomed (moguls behave very differently)
    mergeMandatory: 3, // [E] mandatory vs optional, merged only as a last resort
    noRunsNominalLengthM: 1000, // relative cluster lengths when no runs are mapped
    placeholderLengthM: 1, // a band with no mapped terrain gets a nominal 1 m groomed cluster
    defaultLiftCapacity: 1500, // [E] skiers/h when neither capacity nor a known type is given
    liftCapacityByType: {
      // [E] typical uphill capacity (skiers/h) by OSM aerialway type
      gondola: 2400,
      mixed_lift: 2400,
      chair_lift: 2000,
      cable_car: 1000,
      funicular: 1500,
      t_bar: 1000,
      drag_lift: 900,
      j_bar: 700,
      platter: 700,
      magic_carpet: 600,
      rope_tow: 500,
      railway: 1200, // engine inputs 27 Sep: rack railway (OpenSkiData lift type "railway"), about 1,000 to 1,500 skiers/h (Gornergrat, Jungfraubahn)
    } as Readonly<Record<string, number>>,
    /** Lift types assumed downloadable when `isDownloadable` is unset. [E] */
    downloadableTypes: ["gondola", "cable_car", "mixed_lift", "funicular", "railway"] as readonly string[], // engine inputs 27 Sep: railway (rack railways such as the Gornergrat or Jungfrau) rides down too
  },
  /** Engine orchestration (moved from engine.ts ENGINE_LOCAL_PARAMS, consolidation 26 Sep). */
  engine: {
    coldStartNaturalRho: 300, // [E] contract Section 7: natural density at cold start (kg/m3)
    coldStartPisteRho: 450, // [S] contract Sections 7 and 23: Pe and Pu copy N at 450 kg/m3 (Keller 2004)
    priorQuantileFloor: 1e-6, // numerical: lowest conditional quantile of the positive branch of the seasonal prior
    anchorNorth: "09-01", // contract Section 7: pre-season anchor date, northern hemisphere
    anchorSouth: "03-01", // contract Section 7: pre-season anchor date, southern hemisphere
    gunsRunningScaleMm: 0.5, // [E] made snow (mm/h) over which the guns-running factor ramps in (smooth stand-in for the boolean)
    windMemoryTauH: 24, // [E] time constant of the within-day transport-wind direction memory (windwardness)
    transportWindKmh: 18, // [E] wind above this (about 5 m/s, the fresh-snow transport threshold) sets the transport direction
    windMemoryPseudoKmh: 10, // [E] pseudo weight so a weak or absent transport wind gives windwardness near 0 (smooth)
    nightSoftMinWidthC: 0.5, // [E] soft-minimum width for the night minimum surface temperature (touring wet index)
    e3WindowFactor: 2, // [E] steady-state ratio of a 72 h sum to the 36 h wet memory (touring E3 proxy)
    runSpeedMPerMin: 350, // [E] default descent speed for run arrival offsets when estimatedMinutes is missing
    runBestHours: 2, // [E] length of a run's published best-hours window
    runMeanWeight: 0.66, // contract Section 15: run quality 0.66 time-weighted mean + 0.34 min
    logOddsProbabilityClamp: 0.01, // [E] probabilities are clamped to [0.01, 0.99] before evidence log-odds
    priorManagedShare: 0.5, // [E] piste basis reads "prior-managed" when made snow is at least half the piste SWE
    maxRunMemories: 200, // [E] run-status streaks carried in the checkpoint (bounded by its cluster list)
    fallbackScoredFrom: 10, // [E] scored hours when neither lift hours nor daylight give any
    fallbackScoredTo: 15, // [E]
    touringBelowSnowlineWeight: 1e-6, // [E] tuned round 1: floor weight of a band below the snowline in the touring cover soft-min (0.02 let a bare band's G dominate the p = 6 soft-min, T6); was 0.02
    // Moved from bare constants in engine.ts (consolidation, 26 Sep).
    skiableG: 0.5, // [E] a member is skiable when G >= 0.5 (evidence log-odds of P(skiable) and a run's pSkiable)
    crowdMin: 1, // contract Section 3: crowdFactor per day is 1 to 2
    crowdMax: 2, // contract Section 3
  },
  /** Explanations (moved from explain.ts EXPLAIN_LOCAL_PARAMS, consolidation 26 Sep). Presentation tunables, [E] unless tagged [S]. */
  explain: {
    /** [E] A factor at or above this value is not called limiting (limitingFactor returns null). */
    limitingMaxValue: 0.95,
    /** [E] Evidence whose summed |log-odds| is below this is too weak to show as a line. */
    minEvidenceLogOdds: 0.05,
    /** [E] Evidence lines kept by default (contract Section 16: the top three). */
    maxEvidenceLines: 3,
    /** [E] Dominant states kept by default. */
    maxDominantStates: 3,
    /** [E] Time blocks kept by default (a day reads in at most four phrases). */
    maxTimeBlocks: 4,
    /** [E] Free-text notes kept by buildExplanations. */
    maxNotes: 6,
    /** [E] Surface quality words, lower bounds on Q, anchored to the Section 5.10 rubric bands. */
    surfaceWords: [
      [0.86, "Excellent"],
      [0.76, "Very good"],
      [0.62, "Good"],
      [0.45, "Fair"],
      [0.3, "Poor"],
    ],
    surfaceWordFloor: "Very poor",
    /** [E] Visibility words: about 20 km clear, 10 km haze, 2.5 km poor on the Section 13 curve. */
    visibilityWords: [
      [0.9, "Good visibility"],
      [0.7, "Flat light or haze"],
      [0.4, "Poor visibility"],
    ],
    visibilityWordFloor: "Very poor visibility",
    /** [E] Wind words: effective wind about 30, 40 and 50 km/h on the Section 13 curve. */
    windWords: [
      [0.9, "Light wind"],
      [0.7, "Breezy"],
      [0.45, "Strong wind"],
    ],
    windWordFloor: "Very strong wind",
    /** [E] Cold words: apparent temperature about -11 C and -15 C on the Section 13 ramp. */
    comfortWords: [
      [0.98, "Comfortable"],
      [0.9, "Cold"],
    ],
    comfortWordFloor: "Bitterly cold",
    /** [E] Rain words above the plain-rain floor (about 0.1 mm and 0.8 mm per hour). */
    rainWords: [
      [0.99, "Dry"],
      [0.9, "Light rain"],
    ],
    rainWordMid: "Rain",
    /** Below the plain-rain floor (1 - rainLossMax) only freezing rain can pull R down. */
    rainWordFloor: "Freezing rain",
    /** [E] Margin under the plain-rain floor before a value reads as freezing rain. */
    freezingRainMargin: 0.01,
    // Moved from bare constants in engine.ts (consolidation, 26 Sep).
    hourlyStateCount: 3, // [E] surface states published per hour point (resort, band and run hourly)
    windHoldNoteProbability: 0.5, // [E] add the "Wind holds likely on upper lifts" note above this top-band hold probability
    limitingStateShare: 0.3, // [E] a run's limiting section reads "slush" or "ice" when those states carry this share
    missingWeatherClassDropShare: 0.25, // [E] review fix 27 Sep: new; the confidence class drops one step when more than a quarter of the scored hours have no weather...
    missingWeatherClassCShare: 0.75, // [E] ...and to C beyond three quarters
    missingWeatherEnvelope: 0.5, // [E] review fix 27 Sep: new; lo and hi widen towards q(1 - 0.5) and q + (100 - q) 0.5 in proportion to the share of scored hours without weather (the range stayed tight on a day with no weather)
  },
  /** Checkpoint policy limits (moved from checkpoint.ts, consolidation 26 Sep). The stored format stays in checkpoint.ts. */
  checkpoint: {
    /**
     * Encoded bytes per location-day. Orchestrator decision (26 Sep): 30,000 so the largest rough
     * terrains (72 units) stay lossless; the snow_checkpoints table caps payloads at 32 KiB. [E]
     */
    budgetBytes: 30000,
    /** Bytes the applied-evidence key list may use inside the budget (most recent keys kept). [E] */
    evidenceKeyBytes: 4096,
    /** Longest evidence key or cluster id carried (characters). Longer ones are dropped. [E] */
    maxKeyChars: 160,
    /** Longest cluster `lastStatus` carried (characters). [E] */
    maxStatusChars: 32,
    /** Largest run-status streak carried (days). [E] */
    maxStreakDays: 400,
    /** Largest unforced-hours count carried; stored to 0.1 h. [E] */
    maxUnforcedHours: 100000,
    /** Largest cluster memory count carried. [E] */
    maxClusters: 256,
    /** Longest optional memory list carried (wind memory, band mids). Review fix 27 Sep. [E] */
    maxMemoryNumbers: 192,
    /** Wind memory resolution and bound (km/h-weighted sums). Review fix 27 Sep. [E] */
    windMemoryStep: 0.1,
    windMemoryLimit: 100000,
    /** Bound on stored band mid elevations (m). Review fix 27 Sep. [E] */
    bandMidLimitM: 10000,
    /** Partial load after a terrain change: a unit is kept when its band mid moved by at most this (m). Review fix 27 Sep. [E] */
    partialLoadBandToleranceM: 50,
    /** v2 bootstrap: air temperature assumed for the density of new snow (C). [E] */
    v2FreshTempC: -5,
    /** v2 bootstrap: accepted weight-sum error before renormalising. [E] */
    v2WeightSumTolerance: 0.001,
    /** v2 bootstrap: anchors closer than this are duplicates (m). [E] */
    v2DuplicateElevationM: 1,
  },
  /**
   * Legacy adapter storage and presentation tunables (moved from legacy-adapter.ts, consolidation
   * 26 Sep); none of them feeds back into a score.
   */
  legacy: {
    /** [E] Surface class shares below this percentage are left out of a stored band (display noise). */
    minSurfaceSharePct: 10,
    /** [E] Dominant surface classes kept per stored band (consumer inventory 6.2: at most 3). */
    maxSurfaceShares: 3,
    /** [E] Time blocks kept per stored band and per run (consumer inventory 6.2: at most 4). */
    maxTimeBlocks: 4,
    /** [E] Stored time-block edges are rounded to this step in hours (half-hour detail is enough). */
    blockStepH: 0.5,
    /** [E] Freezing level relative to a band is rounded to this step in metres. */
    freezingLevelStepM: 10,
    /**
     * [E] Byte budget for one sport's stored bands in a day. Three sports at 650 bytes plus the
     * JSON wrapper stay under 2,000 bytes (consumer inventory 6.2 budgets about 2 KB per day).
     */
    bandBudgetBytes: 650,
    /** [E] Piste extent factor below which the limited-terrain flag is set (X costs at least 1 %). */
    limitedTerrainX: 0.99,
    /** [E] Decimal places for the extent factor and open fraction in provenance. */
    fractionDecimals: 2,
    /** [E] Decimal places for depths in metres in provenance (centimetre resolution). */
    depthDecimals: 2,
  },
} as const;

/** Skiability anchors (kg/m2 SWE). G = 0 below S0, 1 from S1 (smoothstep in ln SWE). [E] anchored to [S] operator minimums. */
export const COVER_ANCHORS: Readonly<Record<Exclude<CoverClass, "glacier">, readonly [number, number]>> = {
  machine_base: [40, 110],
  graded_meadow: [58, 187], // tuned round 6: the deep-dive worked values (20/30/40/50 cm settled -> 0.07/0.51/0.85/0.99) at 350 kg/m3, the density used for offpiste_smooth; groomed natural snow is denser than 330, and at [55, 175] the grooming-order fix lifted 10 cm of natural on unequipped runs back over 15 (P5a 0 to 15, P6 unequipped 70 to 80); was [55, 175]. Tuned round 2: the sourced depth anchors (17 cm and 53 cm of settled natural snow) at the engine's settled density of about 330 kg/m3 instead of 300; 40 cm of natural reads thin in places (G about 0.85, deep dive Section 5), P6 unequipped; was [50, 160]
  stony: [90, 250],
  rocky: [180, 500],
  offpiste_smooth: [78, 220], // tuned round 6: the untracked area of a unit now enters the best quarter at its untracked quality (engine.ts), which lifted 30 cm settled plus 20 cm fresh above marginal again (O8 smooth 30 to 50); 22 and 63 cm settled at 350 kg/m3; was [72, 210]. Tuned round 2: the sourced depth anchors (20 cm and 60 cm settled) at the engine's settled density of about 350 kg/m3 (as graded_meadow); round 1 [70, 200] still read 30 cm settled plus 20 cm fresh as more than marginal (O8 smooth 30 to 50); was [60, 180] before round 1
  offpiste_mixed: [120, 360],
  offpiste_rough: [250, 650],
};

/** National snowmaking terrain shares (fraction of piste area equipped). */
export const NATIONAL_SNOWMAKING_SHARE: Readonly<Record<string, number>> = {
  AT: 0.72, CH: 0.54, FR: 0.38, DE: 0.25, IT: 0.6, ES: 0.4, AD: 0.4,
  US: 0.17, CA: 0.2, AU: 0.15, NZ: 0.2, JP: 0.2, KR: 0.4, CN: 0.6,
  NO: 0.4, SE: 0.4, FI: 0.4, CL: 0.15, AR: 0.15, GB: 0.05,
  SI: 0.5, SK: 0.6, CZ: 0.6, PL: 0.6, BG: 0.4, RO: 0.4, GE: 0.2, TR: 0.3, IR: 0.1, LB: 0.1, MA: 0.05,
};
export const DEFAULT_SNOWMAKING_SHARE = 0.3; // [E]
/** US sub-regional overrides by state prefix in location region/country data. [S NSAA 2023] */
export const US_REGION_SNOWMAKING_SHARE: Readonly<Record<string, number>> = {
  rockies: 0.1, northeast: 0.5, southeast: 0.8, midwest: 0.8, west: 0.15,
};

/** NWP snow-depth observation parameters by snow region group. [E] to fit on the certification set. */
export type NwpObservationParams = Readonly<{ alpha: number; beta: number; a: number; sigma: number; family: "icon" | "gfs" }>;
// Tuned round 1: ICON-family beta 8 -> 25 per m. With beta 8 a 10 cm model reading meant a 90 %
// chance of the model reading zero, so the positive-branch hurdle term P(raw > 0 | d) pushed thin
// snow up by about three times; 25 puts the 50 % point at 12 cm, in line with ICON's thin-snow skill.
// Tuned round 2: north-america beta 2.2 -> 1.0 per m. GFS snow depth in North American mountains has a
// median bias of about -0.55 m (deep dive Section 4), so a 0 m reading is weak evidence against a real
// pack: with 2.2, eight daily zero readings pulled a true 0.8 m pack to 0.3 to 0.4 m (P14 80 to 90).
export const NWP_OBSERVATION_PARAMS: Readonly<Record<string, NwpObservationParams>> = {
  alps: { alpha: 3.0, beta: 25.0, a: 0, sigma: 0.45, family: "icon" },
  pyrenees: { alpha: 3.0, beta: 25.0, a: 0, sigma: 0.45, family: "icon" },
  iberia: { alpha: 3.0, beta: 25.0, a: 0, sigma: 0.5, family: "icon" },
  "eastern-europe": { alpha: 3.0, beta: 25.0, a: 0, sigma: 0.5, family: "icon" },
  caucasus: { alpha: 3.0, beta: 25.0, a: 0, sigma: 0.55, family: "icon" },
  "north-africa": { alpha: 3.0, beta: 25.0, a: 0, sigma: 0.6, family: "icon" },
  scandinavia: { alpha: 3.0, beta: 6.0, a: -0.35, sigma: 0.6, family: "icon" },
  scotland: { alpha: 3.0, beta: 6.0, a: -0.35, sigma: 0.6, family: "icon" },
  "middle-east": { alpha: 3.0, beta: 6.0, a: -0.35, sigma: 0.7, family: "icon" },
  "north-america": { alpha: 1.5, beta: 1.0, a: -0.9, sigma: 0.9, family: "gfs" }, // tuned round 2: beta 2.2 -> 1.0 per m (see note above)
  japan: { alpha: 2.2, beta: 3.5, a: -0.35, sigma: 0.75, family: "gfs" },
  asia: { alpha: 2.2, beta: 3.5, a: -0.35, sigma: 0.75, family: "gfs" },
  "southern-hemisphere": { alpha: 1.5, beta: 2.0, a: -0.3, sigma: 0.9, family: "gfs" },
};
export const DEFAULT_NWP_OBSERVATION_PARAMS: NwpObservationParams = NWP_OBSERVATION_PARAMS["southern-hemisphere"];

/** Rubric anchor bands (0..100) documented in docs/features/ski-scoring-v3.md sections 10 and 11. */
export const RUBRIC_VERSION = "ski-v3-rubric-2026-09-26";
